import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Button, Icon, cn, documentFiles, filesFromDataTransfer } from '@dataroom/shared';

interface UploadZoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

/**
 * The seller's ONE drop zone for their whole document library: many files or
 * whole folders at once (dropped folders are walked, OS clutter skipped). Drag
 * and drop is the progressive enhancement; the always-present,
 * keyboard-focusable "Choose files" and "Choose a folder" buttons are the
 * accessible path.
 */
export function UploadZone({ onFiles, disabled = false }: UploadZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  async function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const files = await filesFromDataTransfer(e.dataTransfer);
    if (files.length > 0) onFiles(files);
  }

  function handlePick(e: ChangeEvent<HTMLInputElement>) {
    const files = documentFiles(e.target.files ?? []);
    if (files.length > 0) onFiles(files);
    e.target.value = ''; // allow re-selecting the same file(s)
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => void handleDrop(e)}
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors',
        dragging ? 'border-primary bg-primary/5' : 'border-input',
        disabled && 'pointer-events-none opacity-60',
      )}
    >
      <Icon name="upload" size={28} className="text-ink-3" />
      <p className="text-base font-semibold">Drop your files or folders here</p>
      <p className="max-w-md text-sm text-muted-foreground">
        Add documents in batches of any size, as you find them — no need to sort or rename first.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button type="button" disabled={disabled} onClick={() => inputRef.current?.click()}>
          Choose files
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => folderRef.current?.click()}
        >
          Choose a folder
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-label="Choose files to submit"
        onChange={handlePick}
      />
      {/* Folder picker. `webkitdirectory`/`directory` have no typed React prop, so
          they are set on the element through a callback ref. */}
      <input
        ref={(el) => {
          folderRef.current = el;
          if (el) {
            el.setAttribute('webkitdirectory', '');
            el.setAttribute('directory', '');
          }
        }}
        type="file"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-label="Choose a folder to submit"
        onChange={handlePick}
      />
      <p className="text-xs text-muted-foreground">Any file type · up to 50 MB per file</p>
    </div>
  );
}
